/**
 * Fork extension point for settings navigation — the ONLY nav file a downstream
 * fork edits to add private settings sections/modules.
 *
 * Upstream ships this EMPTY. `nav-registry.ts` splices EXTENSION_SETTINGS_SECTIONS
 * into `settingsSections`, and everything derives from that (SETTINGS_ALL,
 * ALL_MODULES, MODULE_MAP, the query functions, `settingsAllModules`), so a fork
 * settings module appears in the sidebar nav + TopBar breadcrumb + settings
 * routing WITHOUT editing nav-registry.ts. Pair with the settings-panel seam
 * (settings/panels.extensions.tsx) to also mount the panel component.
 *
 * Fork example (in the fork's copy of this file):
 *   import { Users } from './icons';
 *   export const EXTENSION_SETTINGS_SECTIONS: SettingsNavSection[] = [
 *     { key: 'crm', label: 'CRM', requireRole: ['super'], items: [
 *       { id: 'settings.crm', label: 'CRM Sync', icon: Users, path: '#/settings/crm',
 *         parent: 'settings', implemented: true },
 *     ] },
 *   ];
 */

import type { SettingsNavSection } from './nav-registry';
import { Cloud, FileText, Mail, ShoppingBag } from './icons';

/** Private settings sections contributed by a downstream fork. Empty upstream. */
export const EXTENSION_SETTINGS_SECTIONS: SettingsNavSection[] = [
  // Feishu — a per-user connection (any internal user connects their OWN account),
  // so no requireRole gate. Panel mounted via settings/panels.extensions.tsx.
  {
    key: 'feishu',
    items: [
      {
        id: 'settings.feishu',
        label: 'Feishu',
        icon: FileText,
        path: '#/settings/feishu',
        parent: 'settings',
        group: 'connections',
        description: 'Connect your Feishu (Lark) knowledge base and cloud docs',
        implemented: true,
      },
    ],
  },
  {
    key: 'email-providers',
    items: [
      {
        id: 'settings.email-providers',
        label: 'Connect Email',
        icon: Mail,
        path: '#/settings/email-providers',
        parent: 'settings',
        group: 'connections',
        description: 'Connect Gmail or Outlook via OAuth',
        implemented: true,
      },
    ],
  },
  // LetPot Admin workspace bindings — per-user letpot-api credentials (any
  // internal user connects their OWN account). Panel via panels.extensions.tsx.
  {
    key: 'provider-bindings',
    items: [
      {
        id: 'settings.provider-bindings',
        label: 'LetPot Admin',
        icon: Cloud,
        path: '#/settings/provider-bindings',
        parent: 'settings',
        group: 'connections',
        description: 'Connect LetPot Admin API workspaces (dashboard, inquiry, sync)',
        implemented: true,
      },
    ],
  },
  // Ecommerce stores — Shopify/Amazon connections (super-only; the API route is
  // requireSuper()). Panel via panels.extensions.tsx.
  {
    key: 'ecommerce',
    requireRole: ['super'],
    items: [
      {
        id: 'settings.ecommerce',
        label: 'E-commerce',
        icon: ShoppingBag,
        path: '#/settings/ecommerce',
        parent: 'settings',
        group: 'connections',
        description: 'Connect Shopify stores for the ecommerce agent tool',
        implemented: true,
        requireRole: ['super'],
      },
    ],
  },
];
